import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import MaterialFolderGrid from "../components/MaterialFolderGrid";
import { getQuestionBankFolders, getQuestionBankInstitutionFolders } from "../helper/apicalls/questionBank";
import { getAllSubjects } from "../helper/apicalls/subject";
import "../styles/MaterialFolder.scss";

const QuestionBank = () => {
  const navigate = useNavigate();
  const { coursekey } = useParams();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const initialTab = searchParams.get("tab") || "course";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [courseFolders, setCourseFolders] = useState([]);
  const [institutionFolders, setInstitutionFolders] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchRootFolders = async () => {
      try {
        setLoading(true);

        const [subjectsRes, foldersRes, instFoldersRes] = await Promise.all([
          getAllSubjects(coursekey),
          getQuestionBankFolders({ title: "course", key: coursekey || "" }),
          getQuestionBankInstitutionFolders({ title: "course", key: coursekey || "" }),
        ]);

        const subjects = Array.isArray(subjectsRes) ? subjectsRes : [];
        const qbFolders = Array.isArray(foldersRes?.data)
          ? foldersRes.data
          : Array.isArray(foldersRes)
          ? foldersRes
          : [];
        const instQbFolders = Array.isArray(instFoldersRes?.data)
          ? instFoldersRes.data
          : Array.isArray(instFoldersRes)
          ? instFoldersRes
          : [];

        const subjectFolders = subjects.map((s) => ({
          ...s,
          _kind: "subject",
        }));
        const materialFolders = qbFolders.map((f) => ({
          ...f,
          _kind: "material",
        }));
        const instMaterialFolders = instQbFolders.map((f) => ({
          ...f,
          _kind: "institution_material",
        }));

        setCourseFolders([...subjectFolders, ...materialFolders]);
        setInstitutionFolders([...subjectFolders, ...instMaterialFolders]);
      } catch (e) {
        console.error("Failed to load question bank folders", e);
        setCourseFolders([]);
        setInstitutionFolders([]);
      } finally {
        setLoading(false);
      }
    };

    if (coursekey) {
      fetchRootFolders();
    }
  }, [coursekey]);

  const handleOpenFolder = (folder) => {
    if (!folder) return;
    if (folder._kind === "subject" && folder.key) {
      navigate(`/question-bank/${coursekey}/${folder.key}?tab=${activeTab}`);
      return;
    }
    if (folder._kind === "material" && (folder.id || folder._id)) {
      const folderId = folder.id || folder._id;
      navigate(`/question-bank-folder/${folderId}`);
      return;
    }
    if (folder._kind === "institution_material" && (folder.id || folder._id)) {
      const folderId = folder.id || folder._id;
      navigate(`/question-bank-folder/${folderId}?type=institution`);
      return;
    }
  };

  const currentFolders = activeTab === "institution" ? institutionFolders : courseFolders;

  return (
    <div className="materials-page">
      <div className="materials-header">
        <h1 className="materials-title">Question Bank</h1>
      </div>

      <div className="materials-tabs">
        <button
          type="button"
          className={`materials-tab ${activeTab === "course" ? "active" : ""}`}
          onClick={() => setActiveTab("course")}
        >
          Course Resources
        </button>
        <button
          type="button"
          className={`materials-tab ${activeTab === "institution" ? "active" : ""}`}
          onClick={() => setActiveTab("institution")}
        >
          Institution Resources
        </button>
      </div>

      {loading ? (
        <div className="materials-loading">Loading question banks...</div>
      ) : (
        <MaterialFolderGrid folders={currentFolders} onOpen={handleOpenFolder} />
      )}
    </div>
  );
};

export default QuestionBank;
